/**
 * ログインのメールアドレス（Supabase Auth）と、プロフィール（users_phase2.email）の食い違いを直す（指示書 A6）。
 *
 * 以前はログインのたびにプロフィールを保存（upsert）して、メールアドレスの変更を反映していた。
 * その保存をやめたため、ルーターのガードがすでに読み込んでいるプロフィールの email と比べ、
 * 食い違うときだけ email を更新する（ふだんは通信0回）。名前・権限は書き換えない。
 * users_phase2.email は、新規登録の重複確認・Stripe の顧客の特定・支払い失敗の連絡などで使われる。
 *
 * - 同じ内容の更新が実行中なら、重ねて送らない（画面の移動が続いても1回）。
 * - 失敗したら、一定時間（既定 60秒）たった後の画面の移動・再ログインで、もう一度試す。
 *   失敗を「試行済み」のまま残さない（タブを開いている間ずっと直らない、を防ぐ）。
 * - 呼び出し側（ガード）はこの更新を待たない。
 */

export const PROFILE_EMAIL_RETRY_AFTER_MS = 60000

const normalize = (email) => String(email || '').trim().toLowerCase()

/** 更新が必要なら、保存するメールアドレスを返す。不要なら null */
export function profileEmailToSync(authEmail, profileEmail) {
  const next = normalize(authEmail)
  if (!next) return null
  if (normalize(profileEmail) === next) return null
  return next
}

/**
 * @param {{
 *   update: (userId: string, email: string) => Promise<{ error?: any }>,
 *   now?: () => number,
 *   retryAfterMs?: number
 * }} deps
 */
export function createProfileEmailSync({ update, now = () => Date.now(), retryAfterMs = PROFILE_EMAIL_RETRY_AFTER_MS }) {
  /** 実行中の更新（同時に重ねない） */
  const inFlight = new Map()
  /** 失敗した時刻（この時間がたつまでは試さない） */
  const failedAt = new Map()

  return function syncProfileEmailIfChanged({ userId, authEmail, profileEmail }) {
    const email = profileEmailToSync(authEmail, profileEmail)
    if (!userId || !email) return Promise.resolve('unchanged')
    const key = `${userId} ${email}`
    if (inFlight.has(key)) return inFlight.get(key)
    const lastFailure = failedAt.get(key)
    if (lastFailure != null && now() - lastFailure < retryAfterMs) return Promise.resolve('skipped')

    const run = (async () => {
      try {
        const { error } = (await update(userId, email)) || {}
        if (error) {
          console.warn('users_phase2 email 同期失敗:', error)
          failedAt.set(key, now())
          return 'failed'
        }
        failedAt.delete(key)
        return 'updated'
      } catch (e) {
        console.warn('users_phase2 email 同期エラー:', e)
        failedAt.set(key, now())
        return 'failed'
      } finally {
        inFlight.delete(key)
      }
    })()
    inFlight.set(key, run)
    return run
  }
}
